import {
    Confirm,
    Dialog,
    eq,
    Flash,
    Form,
    format,
    Instance,
    Objekt,
    Promize,
    Table,
} from '@siposdani87/sui-js';
import { saveAs } from 'file-saver';
import { app } from '../app';
import { LanguageService } from '@services/languageService';
import { MemberService } from '@services/memberService';
import { UserService } from '@services/userService';
import { initTable } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class MembersIndexController extends DashboardController {
    static readonly inject = [
        'instances',
        'memberService',
        'userService',
        'languageService',
    ] as const;

    organizationId!: string;
    hasAccessMemberEdit!: boolean;
    membersTable!: Table;
    private flash: Flash;
    private dialog: Dialog;
    private confirm: Confirm;

    constructor(
        instances: Instance,
        private memberService: MemberService,
        private userService: UserService,
        private languageService: LanguageService,
    ) {
        super(instances);

        this.flash = instances.flash;
        this.dialog = instances.dialog;
        this.confirm = instances.confirm;
    }

    protected override _initLayout(): void {
        this.organizationId = this.state.getParam('organizationId');

        this._initToolbarButtons();
        this._initFilterForm();
        this._initAccess();

        this._drawContent();
    }

    protected override _drawContent(): Promize | boolean {
        this._initMemberTable();
        return true;
    }

    private _initAccess(): void {
        this.hasAccessMemberEdit = this.userService.hasAccessByOrganization(
            this.organizationId,
            'members_write',
        );
    }

    private _initMemberTable(): void {
        this.membersTable = initTable(
            this.membersTable,
            this.dom,
            '.members-table',
            this.memberService,
            {
                entityKey: 'members',
                fetchData: (params) =>
                    this.memberService.getAllByOrganization(
                        this.organizationId,
                        params,
                    ),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('group_ids', this.groupIds);
                },
            },
            [
                {
                    style: () => {
                        return [
                            'archive',
                            this.languageService.translate(
                                'buttons.move_to_guests',
                            ),
                            false,
                            !this.hasAccessMemberEdit,
                        ];
                    },
                    click: this._downgradeMemberConfirm.bind(this),
                },
                {
                    style: () => {
                        return [
                            'edit',
                            this.languageService.translate('buttons.edit'),
                            false,
                            !this.hasAccessMemberEdit,
                        ];
                    },
                    click: (member) => {
                        this.memberService.editDialog(member).then(() => {
                            this._drawContent();
                        });
                    },
                },
            ],
        )!;
    }

    private _initToolbarButtons(): void {
        this.helper.fabButton('.btn-members-export', this.dom, () => {
            this._exportMembersDialog();
        });

        this.helper.fabButton('.btn-members-new', this.dom, () => {
            this.memberService
                .newDialogByOrganization(this.organizationId)
                .then(() => {
                    this._drawContent();
                });
        });

        this.helper.iconButton('.btn-organizations-show', this.dom, () => {
            this.state.goBack('organizations.show', {
                organizationId: this.organizationId,
            });
        });
    }

    private _exportMembersDialog(): void {
        this.dialog
            .loadTemplate(
                format('/client/v1/organizations/{0}/members/export.html', [
                    this.organizationId,
                ]),
            )
            .then((dialogKnot) => {
                const form = new Form(dialogKnot);
                form.on('submit', (formData: Objekt) => {
                    const memberData = formData.get<Objekt>('member');
                    if (eq(memberData.get<string>('export'), 'list')) {
                        this._exportMembersList(memberData);
                    } else if (eq(memberData.get<string>('export'), 'contact')) {
                        this._exportMembersContact(memberData);
                    }
                });
                form.on('reset', () => {
                    this.dialog.close();
                });
                this.dialog.open();
            });
    }

    private _exportMembersList(member: Objekt): void {
        this.memberService.exportList(this.organizationId, member).then(
            (response, filename) => {
                saveAs(response.get<Blob>('raw'), filename);
                this.dialog.close();
            },
            (response) => {
                this.flash.addMessage(response.get('message'));
            },
        );
    }

    private _exportMembersContact(member: Objekt): void {
        this.memberService.exportContact(this.organizationId, member).then(
            (response, filename) => {
                saveAs(response.get<Blob>('raw'), filename);
                this.dialog.close();
            },
            (response) => {
                this.flash.addMessage(response.get('message'));
            },
        );
    }

    private _downgradeMemberConfirm(member: Objekt): void {
        this.confirm.load(
            this.languageService.translate(
                'members_controller.downgrade.confirm',
                {
                    member_name: member.get<string>('name'),
                },
            ),
            this.languageService.translate('buttons.move'),
            this.languageService.translate('buttons.cancel'),
            '',
            'warning',
        );
        this.confirm.on('ok', () => {
            this.memberService.downgrade(member).then(
                (response) => {
                    this.flash.addMessage(response.get('message'));
                    this.membersTable.refresh();
                },
                (response) => {
                    this.flash.addMessage(response.get('message'));
                },
            );
        });
        this.confirm.open();
    }
}

export const membersIndexController = app.controller(
    'membersIndexController',
    MembersIndexController,
);
